import { defineStore } from 'pinia'
import { authService } from '@/api/auth'
import type { LoginRequest } from '@/types/auth'
import { updateAbility } from '@/plugins/casl'

interface User {
  id: string
  name: string
  email: string
  roleName: string
  photoPath: string | null
  permissions: string[]
}

interface AuthState {
  user: User | null
  isLoading: boolean
  error: string | null
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    isLoading: false,
    error: null
  }),

  getters: {
    isAuthenticated: (state): boolean => !!state.user,
    currentUser: (state): User | null => state.user,
    roleName: (state): string => state.user?.roleName || '',
    permissions: (state): string[] => state.user?.permissions || [],
    isAdmin: (state): boolean => state.user?.roleName === 'Admin'
  },

  actions: {
    async login(credentials: LoginRequest): Promise<{ success: boolean; message: string }> {
      this.isLoading = true
      this.error = null

      try {
        const data = await authService.login(credentials)

        if (data.code === 200 && data.result) {
          localStorage.setItem('accessToken', data.result.token)
          localStorage.setItem('refreshToken', data.result.refreshToken)

          const profile = await authService.getCurrentUser()
          const p = profile.result
          const permissions = p?.permissions ?? data.result.permissions ?? []

          this.user = {
            id: p?.id ?? '',
            name: p?.name || credentials.email.split('@')[0],
            email: p?.email || credentials.email,
            roleName: p?.role?.name || data.result.roleName,
            photoPath: p?.photoPath ?? null,
            permissions
          }
          updateAbility(permissions)

          return { success: true, message: 'Login successful' }
        } else {
          this.error = data.message || 'Login failed'
          return { success: false, message: this.error || 'Login failed' }
        }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Login failed'
        return { success: false, message: this.error }
      } finally {
        this.isLoading = false
      }
    },

    async logout(): Promise<void> {
      const refreshToken = localStorage.getItem('refreshToken')

      if (refreshToken) {
        try {
          await authService.logout(refreshToken)
        } catch {
          // Ignore logout errors
        }
      }

      // Clear local storage
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      this.user = null
      this.error = null
      updateAbility([])
    },

    async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
      this.isLoading = true
      this.error = null

      try {
        const data = await authService.forgotPassword(email)
        if (data.code === 200) {
          return { success: true, message: data.message || 'Request sent' }
        }
        this.error = data.message || 'Request failed'
        return { success: false, message: this.error }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Request failed'
        return { success: false, message: this.error }
      } finally {
        this.isLoading = false
      }
    },

    async hydrateUser(): Promise<void> {
      if (!localStorage.getItem('accessToken')) return

      try {
        const profileRes = await authService.getCurrentUser()
        if (profileRes.code !== 200 || !profileRes.result) return

        const permissions = profileRes.result.permissions ?? []

        this.user = {
          id: profileRes.result.id,
          name: profileRes.result.name || profileRes.result.email,
          email: profileRes.result.email,
          roleName: profileRes.result.role?.name || '',
          photoPath: profileRes.result.photoPath ?? null,
          permissions
        }
        updateAbility(permissions)
      } catch {
        // 401s are handled by the axios refresh interceptor; anything else we swallow.
      }
    },

    async resetPassword(
      token: string,
      newPassword: string
    ): Promise<{ success: boolean; message: string }> {
      this.isLoading = true
      this.error = null

      try {
        const data = await authService.resetPassword(token, newPassword)
        if (data.code === 200) {
          return { success: true, message: data.message || 'Password reset' }
        }
        this.error = data.message || 'Reset failed'
        return { success: false, message: this.error }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Reset failed'
        return { success: false, message: this.error }
      } finally {
        this.isLoading = false
      }
    },

    hasPermission(permission: string): boolean {
      return this.permissions.includes(permission)
    },

    clearError(): void {
      this.error = null
    }
  }
})
